/**
 * DeadlinesCalendar — top-of-Dashboard widget.
 * Shows the user's compliance deadlines (Imposta di Soggiorno, ROSS1000,
 * extinguisher, annual declaration) with smart status badges,
 * inline settings dialog, and direct CTAs per item.
 */
import { useEffect, useState } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import {
  Receipt,
  ChartLine,
  Fire,
  FileText,
  WarningCircle,
  CheckCircle,
  Gear,
  ArrowRight,
  ArrowSquareOut,
  Calendar,
} from '@phosphor-icons/react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ICONS = { Receipt, ChartLine, Fire, FileText };

const STATUS_STYLE = {
  overdue: { dot: 'bg-red-600', chip: 'bg-red-50 text-red-700 border-red-200', label: 'SCADUTA' },
  today: { dot: 'bg-red-600 animate-pulse', chip: 'bg-red-50 text-red-700 border-red-200', label: 'OGGI' },
  urgent: { dot: 'bg-amber-500 animate-pulse', chip: 'bg-amber-50 text-amber-800 border-amber-200', label: 'URGENTE' },
  warning: { dot: 'bg-amber-400', chip: 'bg-amber-50 text-amber-700 border-amber-200', label: 'ATTENZIONE' },
  info: { dot: 'bg-zinc-400', chip: 'bg-zinc-50 text-zinc-600 border-zinc-200', label: 'IN AGENDA' },
  setup: { dot: 'bg-blue-400', chip: 'bg-blue-50 text-blue-700 border-blue-200', label: 'CONFIGURA' },
};

const fmtDue = (iso) => {
  if (!iso) return '—';
  try {
    return new Date(iso + 'T12:00:00').toLocaleDateString('it-IT', { day: 'numeric', month: 'short', year: 'numeric' });
  } catch {
    return iso;
  }
};

const daysLeftLabel = (n) => {
  if (n === null || n === undefined) return '—';
  if (n < 0) return `${Math.abs(n)}gg fa`;
  if (n === 0) return 'oggi';
  if (n === 1) return 'domani';
  if (n < 30) return `tra ${n}gg`;
  const m = Math.round(n / 30);
  return `~${m} ${m === 1 ? 'mese' : 'mesi'}`;
};

export default function DeadlinesCalendar() {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [savingSettings, setSavingSettings] = useState(false);
  const [draft, setDraft] = useState({ imposta_soggiorno_freq: 'trimestrale', estintore_last_check: '', enabled: true });

  const fetchData = async () => {
    try {
      const res = await axios.get(`${API}/deadlines`, { withCredentials: true });
      setData(res.data);
      setDraft({
        imposta_soggiorno_freq: res.data.settings?.imposta_soggiorno_freq || 'trimestrale',
        estintore_last_check: res.data.settings?.estintore_last_check || '',
        enabled: res.data.settings?.enabled !== false,
      });
    } catch {
      // Silent fail (user not authed yet, etc.)
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchData(); }, []);

  const saveSettings = async () => {
    setSavingSettings(true);
    try {
      await axios.put(`${API}/deadlines/settings`, draft, { withCredentials: true });
      toast.success('Impostazioni salvate');
      setSettingsOpen(false);
      await fetchData();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Errore nel salvare');
    } finally {
      setSavingSettings(false);
    }
  };

  if (loading || !data) return null;

  const items = data.items || [];
  const alertsCount = data.alerts_count || 0;

  const handleAction = (d) => {
    if (d.settings_action) {
      setSettingsOpen(true);
      return;
    }
    if (d.external && d.action_url) {
      window.open(d.action_url, '_blank', 'noopener,noreferrer');
      return;
    }
    navigate(d.action_url || '/dashboard');
  };

  return (
    <div className="mb-6 sm:mb-8" data-testid="deadlines-calendar">
      {/* Header bar — matches BigCounters typography (text-[10px]/font-black/uppercase) */}
      <div className="flex items-end justify-between mb-3 sm:mb-5 gap-3">
        <div>
          <p className="text-[10px] sm:text-[11px] font-black uppercase tracking-[0.22em] text-blue-600 mb-1.5">
            Calendario scadenze
          </p>
          <h2 className="text-base sm:text-lg font-black tracking-tight text-zinc-900 flex items-center gap-2 leading-tight">
            <Calendar size={20} weight="bold" className="text-zinc-900" />
            Adempimenti fiscali e amministrativi
            {alertsCount > 0 && (
              <span className="inline-flex items-center gap-1 ml-1 bg-red-600 text-white text-[10px] font-black uppercase tracking-wider px-2 py-0.5" data-testid="deadlines-badge">
                <WarningCircle size={11} weight="fill" /> {alertsCount} {alertsCount === 1 ? 'urgente' : 'urgenti'}
              </span>
            )}
          </h2>
          <p className="text-[11px] text-zinc-500 mt-1 leading-snug">
            Imposta soggiorno · Rilevazione ISTAT (Ross1000 o software regionali analoghi) · Estintore · Dichiarazione annuale
          </p>
        </div>
        <button
          onClick={() => setSettingsOpen(true)}
          className="text-[10px] font-black uppercase tracking-wider text-zinc-700 hover:text-zinc-900 inline-flex items-center gap-1.5 flex-shrink-0 border border-zinc-300 hover:border-zinc-900 px-2.5 py-1.5 transition-colors"
          data-testid="deadlines-settings-btn"
        >
          <Gear size={12} weight="bold" /> Impostazioni
        </button>
      </div>

      {/* Cards grid — matches BigCounters: gap-3 sm:gap-5, 4 cols on lg */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-5" data-testid="deadlines-grid">
        {items.map((d) => {
          const Icon = ICONS[d.icon] || Calendar;
          const st = STATUS_STYLE[d.status] || STATUS_STYLE.info;
          const isUrgent = d.alert || d.status === 'urgent' || d.status === 'today' || d.status === 'overdue';
          return (
            <div
              key={d.id}
              className={`relative bg-white border-l-4 ${isUrgent ? 'border-l-red-600' : 'border-l-blue-500'} border-y border-r border-zinc-200 hover:border-zinc-400 transition-colors p-5 sm:p-6 flex flex-col`}
              data-testid={`deadline-card-${d.kind}`}
            >
              {/* Top row: icon + status chip */}
              <div className="flex items-start justify-between mb-3">
                <div className={`w-10 h-10 ${isUrgent ? 'bg-red-50 text-red-700' : 'bg-blue-50 text-blue-700'} flex items-center justify-center flex-shrink-0`}>
                  <Icon size={18} weight="bold" />
                </div>
                <span className={`inline-flex items-center gap-1 text-[10px] font-black uppercase tracking-wider border px-1.5 py-0.5 ${st.chip}`}>
                  <span className={`w-1.5 h-1.5 rounded-full ${st.dot}`} /> {st.label}
                </span>
              </div>
              {/* Title */}
              <p className="text-sm sm:text-base font-black text-zinc-900 leading-tight tracking-tight mb-1.5">
                {d.title}
              </p>
              {/* Description */}
              <p className="text-[11px] text-zinc-500 leading-relaxed mb-4 flex-1">
                {d.description}
              </p>
              {/* Due date row */}
              {d.due_date && (
                <div className="border-t border-dashed border-zinc-200 pt-3 mb-3">
                  <p className="text-[10px] text-zinc-400 uppercase tracking-[0.18em] font-black mb-0.5">Scade</p>
                  <p className={`text-sm font-black tabular-nums ${isUrgent ? 'text-red-700' : 'text-zinc-900'}`}>
                    {fmtDue(d.due_date)} <span className="text-zinc-400 font-bold text-xs">· {daysLeftLabel(d.days_left)}</span>
                  </p>
                </div>
              )}
              {/* CTA */}
              <button
                onClick={() => handleAction(d)}
                className={`mt-auto inline-flex items-center justify-between text-[10px] font-black uppercase tracking-wider w-full px-3 py-2.5 transition-colors ${
                  isUrgent ? 'bg-zinc-900 hover:bg-zinc-800 text-white' : 'bg-blue-50 hover:bg-blue-100 text-blue-900'
                }`}
                data-testid={`deadline-action-${d.kind}`}
              >
                {d.action_label}
                {d.external ? <ArrowSquareOut size={11} weight="bold" /> : <ArrowRight size={11} weight="bold" />}
              </button>
            </div>
          );
        })}
      </div>

      {/* Settings dialog */}
      <Dialog open={settingsOpen} onOpenChange={setSettingsOpen}>
        <DialogContent className="rounded-none max-w-md" data-testid="deadlines-settings-dialog">
          <DialogHeader>
            <DialogTitle className="text-lg font-black tracking-tight">Impostazioni scadenze</DialogTitle>
          </DialogHeader>
          <div className="space-y-5 mt-2">
            <div>
              <Label className="text-xs font-bold uppercase tracking-wider text-zinc-700">Imposta di soggiorno</Label>
              <p className="text-xs text-zinc-500 mt-1 mb-3">Frequenza con cui rendiconti l'imposta al Comune.</p>
              <div className="grid grid-cols-2 gap-2">
                {[
                  { v: 'mensile', label: 'Mensile', sub: 'Alert 3gg prima fine mese' },
                  { v: 'trimestrale', label: 'Trimestrale', sub: 'Alert 7gg prima fine Q' },
                ].map((o) => (
                  <button
                    key={o.v}
                    type="button"
                    onClick={() => setDraft((d) => ({ ...d, imposta_soggiorno_freq: o.v }))}
                    className={`border px-3 py-3 text-left transition-colors ${
                      draft.imposta_soggiorno_freq === o.v
                        ? 'border-zinc-900 bg-zinc-900 text-white'
                        : 'border-zinc-200 hover:border-zinc-400 text-zinc-900'
                    }`}
                    data-testid={`freq-${o.v}`}
                  >
                    <p className="text-sm font-black tracking-tight">{o.label}</p>
                    <p className={`text-[10px] mt-0.5 ${draft.imposta_soggiorno_freq === o.v ? 'text-zinc-300' : 'text-zinc-500'}`}>{o.sub}</p>
                  </button>
                ))}
              </div>
            </div>

            <div>
              <Label htmlFor="estintore-date" className="text-xs font-bold uppercase tracking-wider text-zinc-700">
                Data ultimo controllo estintore
              </Label>
              <p className="text-xs text-zinc-500 mt-1 mb-2">Il prossimo controllo viene calcolato a 6 mesi.</p>
              <Input
                id="estintore-date"
                type="date"
                value={draft.estintore_last_check || ''}
                onChange={(e) => setDraft((d) => ({ ...d, estintore_last_check: e.target.value }))}
                max={new Date().toISOString().slice(0, 10)}
                className="rounded-none"
                data-testid="estintore-input"
              />
            </div>

            <div className="flex items-center justify-between border-t border-zinc-200 pt-4">
              <div>
                <p className="text-sm font-bold text-zinc-900">Notifiche email</p>
                <p className="text-xs text-zinc-500">Promemoria automatici quando si avvicina una scadenza.</p>
              </div>
              <button
                type="button"
                role="switch"
                aria-checked={draft.enabled}
                onClick={() => setDraft((d) => ({ ...d, enabled: !d.enabled }))}
                className={`relative inline-flex h-6 w-11 items-center transition-colors ${draft.enabled ? 'bg-emerald-500' : 'bg-zinc-300'}`}
                data-testid="enabled-switch"
              >
                <span className={`inline-block h-5 w-5 transform bg-white transition-transform ${draft.enabled ? 'translate-x-5' : 'translate-x-0.5'}`} />
              </button>
            </div>

            <div className="flex gap-2 pt-2">
              <Button
                onClick={() => setSettingsOpen(false)}
                variant="outline"
                className="flex-1 rounded-none border-zinc-300 font-bold text-xs uppercase tracking-wider"
              >
                Annulla
              </Button>
              <Button
                onClick={saveSettings}
                disabled={savingSettings}
                className="flex-1 rounded-none bg-zinc-900 hover:bg-zinc-800 text-white font-bold text-xs uppercase tracking-wider disabled:opacity-50"
                data-testid="deadlines-settings-save"
              >
                {savingSettings ? 'Salvataggio...' : 'Salva'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
